/**
 * FeedbackPickerImageCell — a single filled cell of the editable screenshots
 * grid on the add-feedback page ({@link FeedbackScreenshotsPickerGrid}).
 *
 * Renders the locally-picked screenshot centered at its natural aspect ratio,
 * with a tap-to-view target and a bin/remove overlay. Mirrors Flutter's `Stack`
 * of the image `GestureDetector` + the bin `GestureDetector`
 * (`lib/presentation/screens/feedback/in_app_feedback_screen.dart:410-441`).
 *
 * The cell fills the square slot the parent {@link FeedbackScreenshotsPickerGrid}
 * `VirtualList` grid mode provides (`itemAspectRatio`, `1`) and centers an inner
 * box at the screenshot's own aspect ratio inside it — exactly how the read-only
 * sibling `FeedbackScreenshotsGrid` wraps `ContentPreview` and how
 * `ContentGridItem` wraps its preview. A landscape screenshot (`aspectRatio > 1`)
 * is shorter than the square cell and sits centered with the cell background
 * above/below (never cropped); a portrait overflows top/bottom and is clipped by
 * `overflow: hidden`. This replaces the previous `object-cover` fill that cropped
 * landscape/portrait screenshots to a square.
 *
 * Pure presentational: owns no state, invokes no use case/service; the parent
 * grid supplies the draft screenshot + the tap/remove callbacks.
 */
import { useCallback } from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { ContentThumbnail } from '@/presentation/feature/content/components/ContentThumbnail';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { qaAttr } from '@/presentation/utils/qaAttr';
import type { LocalFeedbackScreenshot } from '@/presentation/feature/feedback/add/hooks/useFeedback';

const BIN_ICON_PATH = '/assets/vectors/bin.svg';

export interface FeedbackPickerImageCellProps {
  screenshot: LocalFeedbackScreenshot;
  index: number;
  onScreenshotTap: (index: number) => void;
  onRemoveTap: (index: number) => void;
}

export const FeedbackPickerImageCell = ({
  screenshot,
  index,
  onScreenshotTap,
  onRemoveTap,
}: FeedbackPickerImageCellProps) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  const handleRemove = useCallback(
    (event: React.MouseEvent) => {
      // Stop the tap from bubbling to the cell's view handler (parity with the
      // separate Flutter GestureDetector, `:423-440`).
      event.stopPropagation();
      onRemoveTap(index);
    },
    [index, onRemoveTap],
  );

  return (
    <div
      {...qaAttr({ id: `feedback-picker-cell-${index}` })}
      role="button"
      tabIndex={0}
      onClick={() => onScreenshotTap(index)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onScreenshotTap(index);
        }
      }}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        borderRadius: responsive.borderRadius.sm,
        background: theme.secondary,
        cursor: 'pointer',
      }}
    >
      {/* Inner box at the screenshot's natural aspect ratio, centered in the
          square cell — mirrors how the read-only FeedbackScreenshotsGrid wraps
          ContentPreview and how ContentGridItem wraps its preview. Landscape
          screenshots (aspectRatio > 1) are shorter than the cell (centered,
          cell background above/below); portrait overflows top/bottom and is
          clipped by overflow:hidden. ContentThumbnail's object-cover no longer
          crops here because its box already matches the media's aspect ratio. */}
      <div style={{ width: '100%', aspectRatio: screenshot.aspectRatio }}>
        <ContentThumbnail
          thumbnailUrl={screenshot.previewUrl}
          qa={{ id: `feedback-picker-thumbnail-${index}` }}
        />
      </div>

      {/* Bin/remove overlay — top-left, matching the Flutter Stack's default
          top-start alignment (`:428-439`). Rendered as a circular container
          tinted `theme.danger` with the bin glyph drawn via the theme-masking
          `AssetIcon` over the transparent-silhouette `bin.svg`, tinted
          `theme.icon.primary` so it stays visible in both themes. Mirrors
          Flutter's `Image.asset('assets/images/bin.png')` (`:430-438`) as a
          themed remove affordance. */}
      <button
        {...qaAttr({ id: `feedback-picker-remove-${index}` })}
        type="button"
        onClick={handleRemove}
        style={{
          position: 'absolute',
          top: responsive.spacing.xs,
          left: responsive.spacing.xs,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: responsive.spacing.xxs,
          border: 'none',
          borderRadius: responsive.borderRadius.round,
          background: theme.danger,
          cursor: 'pointer',
        }}
      >
        <AssetIcon
          path={BIN_ICON_PATH}
          width={responsive.icon.small}
          height={responsive.icon.small}
          color={theme.icon.primary}
        />
      </button>
    </div>
  );
};
